import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import PostedJackpotCard from '../components/PostedJackpotCard';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  UserCheck,
  UserX,
  UserPlus,
  Gamepad2,
  PlayCircle,
  Clock,
  CreditCard,
  RefreshCw,
  Bell,
  BellOff,
  Smartphone,
  AlertTriangle,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, subValue, color = 'amber', onClick }) {
  const colors = {
    amber: 'bg-amber-500/10 text-amber-500',
    green: 'bg-green-500/10 text-green-500',
    red: 'bg-red-500/10 text-red-500',
    blue: 'bg-blue-500/10 text-blue-500',
  };

  return (
    <div 
      className={`bg-gray-800 rounded-xl p-5 ${onClick ? 'cursor-pointer hover:bg-gray-700 transition-colors' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAdmin } = useAuth();

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard');
      setStats(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <button
          onClick={fetchDashboard}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* User Stats - Admin Only */}
      {isAdmin && (
        <>
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">User Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats?.users?.total || 0} color="amber" onClick={() => navigate('/users')} />
              <StatCard icon={UserCheck} label="Active Users" value={stats?.users?.active || 0} color="green" onClick={() => navigate('/users')} />
              <StatCard icon={UserX} label="Banned Users" value={stats?.users?.banned || 0} color="red" onClick={() => navigate('/users?filter=banned')} />
              <StatCard
                icon={UserPlus}
                label="New This Month"
                value={stats?.users?.newThisMonth || 0}
                subValue={`${stats?.users?.newToday || 0} today`}
                color="blue"
                onClick={() => navigate('/users?filter=new')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard icon={CreditCard} label="Linked to Player's Club" value={stats?.users?.linkedToPlayersClub || 0} color="amber" onClick={() => navigate('/users?filter=linked')} />
            <StatCard icon={Users} label="Staff & Admins" value={(stats?.users?.admins || 0) + (stats?.users?.staff || 0)} subValue={`${stats?.users?.admins} admins, ${stats?.users?.staff} staff`} color="blue" onClick={() => navigate('/users?filter=staff')} />
          </div>
        </>
      )}

      {/* Game Stats - Visible to all */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Live Games</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={Gamepad2} label="Active Games" value={stats?.games?.active || 0} color="amber" onClick={() => navigate('/games')} />
          <StatCard icon={PlayCircle} label="Players Seated" value={stats?.games?.playing || 0} color="green" onClick={() => navigate('/games')} />
          <StatCard icon={Clock} label="Players Waiting" value={stats?.games?.waiting || 0} color="blue" onClick={() => navigate('/games')} />
        </div>
      </div>

      {/* Push Notification Stats - Admin Only */}
      {isAdmin && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Push Notifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={Bell} 
              label="Opted In" 
              value={stats?.notifications?.optedIn || 0} 
              subValue="Notifications enabled"
              color="amber" 
            />
            <StatCard 
              icon={Smartphone} 
              label="With Valid Token" 
              value={stats?.notifications?.withValidToken || 0} 
              subValue="Can receive pushes"
              color="green" 
            />
            <StatCard 
              icon={BellOff} 
              label="Missing Token" 
              value={stats?.notifications?.missingTokens || 0} 
              subValue="Need app update"
              color={stats?.notifications?.missingTokens > 0 ? "red" : "green"} 
            />
            <StatCard 
              icon={Smartphone} 
              label="Token Sources" 
              value={stats?.notifications?.totalDeviceTokens || 0} 
              subValue={`${stats?.notifications?.totalPrefTokens || 0} in prefs`}
              color="blue" 
            />
          </div>
          {stats?.notifications?.missingTokens > 0 && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-500 mt-0.5" size={20} />
                <div>
                  <p className="text-amber-400 font-medium">
                    {stats.notifications.missingTokens} user{stats.notifications.missingTokens !== 1 ? 's' : ''} opted in but can't receive notifications
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    These users have notifications enabled but no device token registered. They need to update to the latest app version (Build 97+) and open the app to register their device.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* The posted Hold'em jackpot. It lives on the dashboard because it is
          republished on marketing assets every day and whoever does that
          should not have to open the Cage Balance Sheet to find it. */}
      <PostedJackpotCard />

      {/* Recent Activity - Admin Only */}
      {isAdmin && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Recent Admin Activity</h2>
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Admin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {stats?.recentActivity?.length > 0 ? (
                    stats.recentActivity.map((activity) => (
                      <tr key={activity.id} className="hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm text-white">{activity.action.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{activity.adminEmail}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{activity.entityType || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{new Date(activity.createdAt).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        No recent activity
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
